import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMeetContext } from '../../../features/Meet/providers/MeetContextProvider';
import { useMediaContext } from '../../../features/Media/providers/MediaContextProvider';
import { MediaDomainEvents } from '../../../features/Media/domain';
import { publish, subscribe, unsubscribe } from '../../../features/EventsBus/services/service';
import { MeetDomainEvents } from '../../../features/Meet/domain';
import dayjs from 'dayjs';
import { useTranslationsContext } from '../../../features/Translations/providers/TranslationsContextProvider';
import { useRemainingTime } from '../../../features/MeetTimeRemaining/hooks/useRemainingTime';
import { useProfessionalAvailabiltiy } from '../../../features/MeetProfessionalAvailability/hooks/useProfessionalAvailabiltiy';
import usePublisher from '../../../features/VideoCall/hooks/usePublisher';
import { MeetRemainingTimeDomainEvents } from '../../../features/MeetTimeRemaining/domain/events';
import { useVideoCallContext } from '../../../features/VideoCall/providers/VideoCallContextProvider';
import { VideoCallEvents } from '../../../features/VideoCall/domain/events';
import { useNavigate } from 'react-router-dom';

export function useWaitingRoom(code?: string, tenant?: string) {
	const navigate = useNavigate();
	const { t } = useTranslationsContext();
	const {
		meet,
		meetReadyForWaiting,
		retrieveMeet,
		professionalAvailable,
		connectionCredentials,
		isProfessional,
		sendPatientWaiting,
		refreshMeet,
	} = useMeetContext();
	const {
		publisher, 
		hideOptionDisabled,
		muteOptionDisabled,
		hiddenCam,
		muted,
		createPublisher, 
		toggleCamVisibility,
		toggleMute
	} = usePublisher();
	const { sessionRef, createSession, disconnectSession } = useVideoCallContext();
	const { acceptedMediaPermissions, closeNoDevicesFoundModal, checkPermissions } = useMediaContext();
	const { openRemainingModal, timerFinished, remainingModal, closeRemainingModal, onTimerFinished } = useRemainingTime();
	const { professionalNotAvailableModal, toggleProfessionalNotAvailableModal, professionalAvailableModal, toggleProfessionalAvailableModal } = useProfessionalAvailabiltiy();
	const [infoOpened, setInfoOpened] = useState(false);
	const [headerHeight, setHeaderHeight] = useState(0);
	const [cardOffsetTop, setCardOffsetTop] = useState(0);
	const [bottomCardHeight, setBottomCardHeight] = useState(0);
	const [connectedToSession, setConnectedToSession] = useState(false);

	const cannotAccess = useMemo(() => {
		return !acceptedMediaPermissions || !meet?.connection || !connectedToSession;
	}, [acceptedMediaPermissions, meet, connectedToSession]);

	const toggleInfo = useCallback(() => {
		setInfoOpened(!infoOpened);
	}, [infoOpened]);

	const notStarted = useMemo(() => meet?.status === 'NOT_STARTED', [meet]);
	const notOnTime = useMemo(() => dayjs(meet?.startDate).diff(dayjs()) > 0, [meet]);

	const roomUrl = useMemo(() => {
		let url = `/room/${code}`;
		if (tenant) {
			url = `${url}/tenant/${tenant}`;
		}
		return url;
	}, [code, tenant]);

	const access = useCallback(() => {
		if (isProfessional) {
			navigate(roomUrl);
			return;
		}

		const cannotAccess = !professionalAvailableModal && notStarted;

		if (notOnTime && !timerFinished && cannotAccess) {
			openRemainingModal();
			return;
		}
		if (!professionalAvailable && cannotAccess) {
			toggleProfessionalNotAvailableModal(true);
			return;
		}
		navigate(roomUrl);
	}, [isProfessional, professionalAvailableModal, notStarted, professionalAvailable, timerFinished, roomUrl, notOnTime]);


	const headerRefChanged = useCallback((node: HTMLElement | null) => {
		if (node) setHeaderHeight(node.clientHeight);
	}, []);

	const onCardRefChanged = useCallback((node: HTMLElement | null) => {
		if (node) setCardOffsetTop(node.getBoundingClientRect().top);
	}, []);

	const onBottomCardRefChanged = useCallback((node: HTMLElement | null) => {
		if (node) setBottomCardHeight(node.clientHeight);
	}, []);
	
	const startDate = useMemo(() => {
		return dayjs(meet?.startDate).format(t('datetime.datetime_format'));
	}, [meet, t]);

	const onPermissionsModalClose = useCallback(() => {
		publish(MediaDomainEvents.permissionsModalClosed);
	}, []);

	const onNoDevicesFoundModalClose = useCallback(() => {
		closeNoDevicesFoundModal();
	}, [closeNoDevicesFoundModal]);

	const initMeet = useCallback(() => {
		if (code) {
			retrieveMeet(code, tenant);
		}
	}, [code, tenant, retrieveMeet]);

	const createSessionWithCredentials = useCallback(() => {
		if (connectionCredentials) {
			publish(VideoCallEvents.sessionConnecting);
			createSession(connectionCredentials);
		} else {
			publish(VideoCallEvents.sessionCreationFailedByCredentials);
		}
	}, [meet, connectionCredentials]);

	const closeProfessionalNotAvailableModal = useCallback(() => {
		toggleProfessionalNotAvailableModal(false);
	}, []);

	const toggleMic = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
		e.stopPropagation();
		toggleMute();
	}, [muted]);

	useEffect(() => {
		if (professionalAvailable) {
			closeProfessionalNotAvailableModal();
			openRemainingModal();
		}
	}, [professionalAvailable]);

	useEffect(() => {
		function professionalAvailableCallback() {
			toggleProfessionalAvailableModal(true);
			refreshMeet();
		}

		subscribe(MeetRemainingTimeDomainEvents.timerFinished, refreshMeet);
		subscribe(MeetDomainEvents.professionalAvailable, professionalAvailableCallback);

		return () => {
			unsubscribe(MeetRemainingTimeDomainEvents.timerFinished, refreshMeet);
			unsubscribe(MeetDomainEvents.professionalAvailable, professionalAvailableCallback);
		};
	}, [refreshMeet]);

	useEffect(() => {
		function retrieveMeetCallback() {
			initMeet();
		}

		subscribe(MeetDomainEvents.retrieveMeet, retrieveMeetCallback);

		return () => {
			unsubscribe(MeetDomainEvents.retrieveMeet, retrieveMeetCallback);
		};
	}, []);

	useEffect(() => {
		subscribe(MeetDomainEvents.retrievedMeet, checkPermissions);

		return () => {
			unsubscribe(MeetDomainEvents.retrievedMeet, checkPermissions);
		};
	}, [acceptedMediaPermissions]);

	useEffect(() => {
		if (!meetReadyForWaiting) {
			return;
		}

		function acceptedMediaPermissionsCallback() {
			if (!sessionRef.current) {
				createSessionWithCredentials();
			} else {
				publish(VideoCallEvents.sessionAlreadyCreated);
			}

			if (!publisher?.current) {
				createPublisher('video-preview');
			} else {
				publish(VideoCallEvents.publisherAlreadyCreated);
			}

			sendPatientWaiting();
		}

		subscribe(MediaDomainEvents.acceptedMediaPermissions, acceptedMediaPermissionsCallback);
		subscribe(MediaDomainEvents.alreadyAcceptedMediaPermissions, acceptedMediaPermissionsCallback);

		return () => {
			unsubscribe(MediaDomainEvents.acceptedMediaPermissions, acceptedMediaPermissionsCallback);
			unsubscribe(MediaDomainEvents.alreadyAcceptedMediaPermissions, acceptedMediaPermissionsCallback);
		};
	  }, [sessionRef, meetReadyForWaiting]);

	useEffect(() => {
		function sessionConnectedCallback() {
			setConnectedToSession(true);
			publish(MeetDomainEvents.waitingRoomReady);
		}

		function sessionLostCallback() {
			setConnectedToSession(false);
		}

		subscribe(VideoCallEvents.sessionConnected, sessionConnectedCallback);
		subscribe(VideoCallEvents.sessionAlreadyCreated, sessionConnectedCallback);
		subscribe(VideoCallEvents.sessionLost, sessionLostCallback);

		return () => {
			unsubscribe(VideoCallEvents.sessionConnected, sessionConnectedCallback);
			unsubscribe(VideoCallEvents.sessionAlreadyCreated, sessionConnectedCallback);
			unsubscribe(VideoCallEvents.sessionLost, sessionLostCallback);
		};
	}, []);

	useEffect(() => {
		localStorage.removeItem('opentok_client_id');
		publish(MeetDomainEvents.waitingRoomAccess);
		publish(MeetDomainEvents.retrieveMeet);

		return () => {
			disconnectSession();
		};
	}, []);

	return {
		cannotAccess,
		infoOpened,
		notStarted,	
		startDate,
		headerHeight,
		cardOffsetTop,
		bottomCardHeight,
		remainingModal,
		professionalNotAvailableModal,
		hideOptionDisabled,
		muteOptionDisabled,
		hiddenCam,
		muted,
		professionalAvailableModal,
		toggleProfessionalAvailableModal,
		toggleCamVisibility,
		toggleMic,
		toggleProfessionalNotAvailableModal,
		onTimerFinished,
		closeRemainingModal,
		toggleInfo,
		access,
		headerRefChanged,
		onCardRefChanged,
		onBottomCardRefChanged,
		onPermissionsModalClose,
		onNoDevicesFoundModalClose,
		initMeet,
	};
}

